import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Grid} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import MenuGroupedCategories from '../../list-menu/partials/MenuGroupedCategories'
import MenuVerticalCard from '../../list-menu/partials/MenuVerticalCard'
import MenuSimpleCard from '../../list-menu/partials/MenuSimpleCard'
import {getTemplateLinks, hasValidPrimaryCard} from '../../list-menu/partials/templates/shared'
import {
    DrawerLinkButtons,
    compactGap,
    desktopOnlyDisplay,
    drawerTemplatePropTypes,
    fullHeightChildSx,
    groupedCategoriesProps,
    mobileOnlyDisplay,
    templateGridGap,
    templateGridGapSx,
} from './shared'

export const getDrawerTemplateFourData = (primarycategory) => {
    const baseCards =
        Array.isArray(primarycategory?.images) && primarycategory.images.length > 0
            ? primarycategory.images
            : [primarycategory?.image_1, primarycategory?.image_2].filter(Boolean)
    const verticalCards = [...baseCards, primarycategory?.image_3].filter(Boolean).slice(0, 3)

    return {
        categoryGroup: primarycategory?.category_group ?? [],
        links: getTemplateLinks(primarycategory),
        verticalCards,
        firstTwoCards: verticalCards.slice(0, 2),
        thirdCard: verticalCards[2] ?? null,
        primaryCard: hasValidPrimaryCard(primarycategory) ? primarycategory.card : null,
    }
}

const VerticalCardBox = ({card, categoryData, locale}) => (
    <Box w="100%" aspectRatio={0.75} sx={fullHeightChildSx}>
        <MenuVerticalCard card={card} categoriesData={categoryData} locale={locale} />
    </Box>
)

VerticalCardBox.propTypes = {
    card: PropTypes.object,
    categoryData: PropTypes.object,
    locale: PropTypes.object,
}

const DrawerTemplateFour = ({primarycategory, categoryData, locale, styles}) => {
    const {categoryGroup, links, verticalCards, firstTwoCards, thirdCard, primaryCard} =
        getDrawerTemplateFourData(primarycategory)

    const groupedCategories = categoryData && categoryGroup.length > 0 && (
        <Box {...groupedCategoriesProps}>
            <MenuGroupedCategories
                groupedCategories={categoryGroup}
                locale={locale}
                categoryData={categoryData}
                styles={styles}
                groupLinkGap={remCalc(8)}
            />
        </Box>
    )

    const linkList = links.length > 0 && (
        <Flex flexDir="column" gap={2}>
            <DrawerLinkButtons
                links={links}
                categoryData={categoryData}
                locale={locale}
                styles={styles}
            />
        </Flex>
    )

    const firstTwoCardsGrid = (keyPrefix) =>
        firstTwoCards.length > 0 && (
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={compactGap}>
                {firstTwoCards.map((card, idx) => (
                    <VerticalCardBox
                        key={card?._metadata?.uid ?? `${keyPrefix}-${idx}`}
                        card={card}
                        categoryData={categoryData}
                        locale={locale}
                    />
                ))}
            </Grid>
        )

    const thirdCardBox = thirdCard && (
        <VerticalCardBox card={thirdCard} categoryData={categoryData} locale={locale} />
    )

    return (
        <Box
            w="100%"
            pb={remCalc(64)}
            sx={{'@media screen and (min-width: 2000px)': {paddingBottom: 'unset'}}}
            data-primarycategory-id={primarycategory?.uid}
        >
            <Grid
                templateColumns={{base: '1fr', sm: '1fr', md: primaryCard ? '1fr' : '1fr 2fr'}}
                gap={templateGridGap}
                sx={templateGridGapSx}
                alignItems="start"
            >
                {!primaryCard && (
                    <Flex
                        display={desktopOnlyDisplay}
                        flexDir="column"
                        gap={templateGridGap}
                        sx={templateGridGapSx}
                        gridColumn="1 / 2"
                    >
                        {groupedCategories}
                        {linkList}
                    </Flex>
                )}

                <Flex
                    display={desktopOnlyDisplay}
                    flexDir="column"
                    gap={compactGap}
                    gridColumn={primaryCard ? '1 / -1' : '2 / 3'}
                >
                    {primaryCard ? (
                        <>
                            {verticalCards.length > 0 && (
                                <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={compactGap}>
                                    {verticalCards.map((card, idx) => (
                                        <VerticalCardBox
                                            key={
                                                card?._metadata?.uid ??
                                                `drawer-temp4-md-vertical-card-${idx}`
                                            }
                                            card={card}
                                            categoryData={categoryData}
                                            locale={locale}
                                        />
                                    ))}
                                </Grid>
                            )}

                            <Box w="100%" aspectRatio={4.9} sx={fullHeightChildSx}>
                                <MenuSimpleCard
                                    card={primaryCard}
                                    categoriesData={categoryData}
                                    locale={locale}
                                    isLarge
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            {firstTwoCardsGrid('drawer-temp4-md-first-two-card')}

                            {thirdCard &&
                                (links.length > 0 ? (
                                    <Grid
                                        templateColumns="repeat(2, minmax(0, 1fr))"
                                        gap={compactGap}
                                    >
                                        {thirdCardBox}
                                    </Grid>
                                ) : (
                                    thirdCardBox
                                ))}
                        </>
                    )}
                </Flex>

                <Flex
                    display={mobileOnlyDisplay}
                    gridColumn="1 / -1"
                    flexDir="column"
                    gap={templateGridGap}
                    sx={templateGridGapSx}
                >
                    {groupedCategories}

                    {(firstTwoCards.length > 0 || thirdCard || primaryCard) && (
                        <Flex flexDir="column" gap={compactGap}>
                            {firstTwoCardsGrid('drawer-temp4-mobile-first-two-card')}
                            {thirdCardBox}
                            {primaryCard && (
                                <Box w="100%" aspectRatio={3} sx={fullHeightChildSx}>
                                    <MenuSimpleCard
                                        card={primaryCard}
                                        categoriesData={categoryData}
                                        locale={locale}
                                        isLarge
                                    />
                                </Box>
                            )}
                        </Flex>
                    )}

                    {!primaryCard && linkList}
                </Flex>
            </Grid>
        </Box>
    )
}

DrawerTemplateFour.propTypes = drawerTemplatePropTypes

export default DrawerTemplateFour
